import { useMemo, useRef } from 'react';
import * as R from 'remeda';
import { QueueAction } from '@rossum/api-client/schemas';
import { Chip, Stack } from '@rossum/ui/material';

// Left padding + right padding - 1x spacing
const PADDING = 10 + 10 - 8;

// Works for single digit number of icons in chip
// Let's hope nobody has 10+ actions
// Both include margin
const CHIP_SIZE = 32 + 8;
const ICON_SIZE = 24 + 8;

export const ActionCell = ({
  actions,
  ActionComponent,
  width,
}: {
  actions: QueueAction[];
  ActionComponent?: React.FC<{ action: QueueAction }> | undefined;
  width?: number | undefined;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const numberOfIconsThatFit = width
    ? Math.floor((width - PADDING) / ICON_SIZE)
    : 0;

  const numberOfIconsThatFitIncludingChip = width
    ? Math.floor((width - PADDING - CHIP_SIZE) / ICON_SIZE)
    : 0;

  const actionsWithKeys = useMemo(
    () =>
      actions.map((action, index) => ({
        ...action,
        key: index,
      })),
    [actions]
  );

  const [firstTwoActions, otherActions] = R.partition(
    actionsWithKeys,
    (_, index) =>
      // This makes sure you don't display chip needlessly even though the last icon would fit if there was no chip.
      actions.length <= numberOfIconsThatFit
        ? true
        : index < numberOfIconsThatFitIncludingChip
  );

  return ActionComponent ? (
    <Stack
      direction="row"
      spacing={1}
      width="100%"
      height="100%"
      alignItems="center"
      ref={ref}
    >
      {firstTwoActions.map(action => (
        <ActionComponent action={action} key={action.key} />
      ))}
      {otherActions.length ? (
        <Chip
          color="default"
          label={`+${otherActions.length}`}
          key="more-actions-chip"
        />
      ) : null}
    </Stack>
  ) : null;
};
